
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Theme type definitions
export interface AdminTheme {
    id: string;
    name: string;
    colors: {
        primary: string;
        primaryHover: string;
        primaryLight: string;
        background: string;
        surface: string;
        surfaceHover: string;
        text: string;
        textSecondary: string;
        border: string;
        accent: string;
        success: string;
        warning: string;
        error: string;
    };
    isDark: boolean;
}

// Predefined themes - simplified to Dark and Light only
export const ADMIN_THEMES: AdminTheme[] = [
    {
        id: 'dark',
        name: 'Dark Mode',
        isDark: true,
        colors: {
            primary: '#6366f1',
            primaryHover: '#4f46e5',
            primaryLight: '#818cf8',
            background: '#0f172a',
            surface: '#1e293b',
            surfaceHover: '#334155',
            text: '#f8fafc',
            textSecondary: '#94a3b8',
            border: '#334155',
            accent: '#22d3ee',
            success: '#22c55e',
            warning: '#f59e0b',
            error: '#ef4444',
        },
    },
    {
        id: 'light',
        name: 'Light Mode',
        isDark: false,
        colors: {
            primary: '#6366f1',
            primaryHover: '#4f46e5',
            primaryLight: '#a5b4fc',
            background: '#f8fafc',
            surface: '#ffffff',
            surfaceHover: '#f1f5f9',
            text: '#0f172a',
            textSecondary: '#64748b',
            border: '#e2e8f0',
            accent: '#0ea5e9',
            success: '#22c55e',
            warning: '#f59e0b',
            error: '#ef4444',
        },
    },
];

// Storage key
const THEME_STORAGE_KEY = 'admin_theme_id';

// Context type
interface AdminThemeContextType {
    theme: AdminTheme;
    setTheme: (themeId: string) => void;
    themes: AdminTheme[];
}

// Create context
const AdminThemeContext = createContext<AdminThemeContextType | undefined>(undefined);

// Provider props
interface AdminThemeProviderProps {
    children: ReactNode;
}

// Provider component
export const AdminThemeProvider: React.FC<AdminThemeProviderProps> = ({ children }) => {
    const [theme, setThemeState] = useState<AdminTheme>(() => {
        // Load saved theme from localStorage
        const savedThemeId = localStorage.getItem(THEME_STORAGE_KEY);
        const savedTheme = ADMIN_THEMES.find(t => t.id === savedThemeId);
        return savedTheme || ADMIN_THEMES[0]; // Default to dark mode
    });

    // Apply theme CSS variables to document
    useEffect(() => {
        const root = document.documentElement;

        // Set CSS custom properties
        Object.entries(theme.colors).forEach(([key, value]) => {
            root.style.setProperty(`--admin-${key}`, value);
        });

        // Set dark mode class
        if (theme.isDark) {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, [theme]);

    const setTheme = (themeId: string) => {
        const newTheme = ADMIN_THEMES.find(t => t.id === themeId);
        if (newTheme) {
            setThemeState(newTheme);
            localStorage.setItem(THEME_STORAGE_KEY, themeId);
        }
    };

    return (
        <AdminThemeContext.Provider value={{ theme, setTheme, themes: ADMIN_THEMES }}>
            {children}
        </AdminThemeContext.Provider>
    );
};

// Custom hook to use theme
export const useAdminTheme = () => {
    const context = useContext(AdminThemeContext);
    if (!context) {
        throw new Error('useAdminTheme must be used within an AdminThemeProvider');
    }
    return context;
};

// Theme selector component - Simple toggle for Dark/Light mode
export const ThemeSelector: React.FC<{ className?: string }> = ({ className = '' }) => {
    const { theme, setTheme } = useAdminTheme();

    const toggleTheme = () => {
        const newTheme = theme.id === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
    };

    return (
        <button
            onClick={toggleTheme}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 ${className}`}
            title={theme.isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
            {/* Sun/Moon icon with animation */}
            <span className="material-icons-round text-lg transition-transform duration-300">
                {theme.isDark ? 'light_mode' : 'dark_mode'}
            </span>

            {/* Label - hidden on small screens */}
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 hidden sm:inline">
                {theme.isDark ? 'Light' : 'Dark'}
            </span>
        </button>
    );
};
